import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  DocumentTextIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  PlayIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  ChartBarIcon,
  UserGroupIcon,
  HashtagIcon,
} from '@heroicons/react/24/outline';
import {
  getDiscoveryCase,
  getCaseDocuments,
  analyzeCase,
  generatePrivilegeLog,
  assignBatesNumbers,
  getProductionSummary,
} from '../services/api';

export default function CaseDashboard() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [caseInfo, setCaseInfo] = useState(null);
  const [stats, setStats] = useState({});
  const [production, setProduction] = useState({});
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingLog, setGeneratingLog] = useState(false);
  const [assigningBates, setAssigningBates] = useState(false);

  useEffect(() => {
    if (!caseId) return;
    const load = async () => {
      setLoading(true);
      try {
        const [caseRes, docsRes, prodRes] = await Promise.all([
          getDiscoveryCase(caseId),
          getCaseDocuments(caseId, { limit: 1 }),
          getProductionSummary(caseId).catch(() => ({})),
        ]);
        setCaseInfo(caseRes.case);
        setStats(docsRes.stats || {});
        setProduction(prodRes || {});
      } catch (err) {
        console.error('Failed to load case:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [caseId]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await analyzeCase(caseId);
      // Refresh stats
      const docsRes = await getCaseDocuments(caseId, { limit: 1 });
      setStats(docsRes.stats || {});
    } catch (err) {
      console.error('Analysis failed:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handlePrivilegeLog = async () => {
    setGeneratingLog(true);
    try {
      const result = await generatePrivilegeLog(caseId, 'csv');
      // Download CSV
      if (result.content) {
        const blob = new Blob([result.content], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `privilege_log_${caseId}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Privilege log generation failed:', err);
    } finally {
      setGeneratingLog(false);
    }
  };

  const handleAssignBates = async () => {
    setAssigningBates(true);
    try {
      await assignBatesNumbers(caseId, 'PROD', 1);
      const prodRes = await getProductionSummary(caseId);
      setProduction(prodRes || {});
    } catch (err) {
      console.error('Bates assignment failed:', err);
    } finally {
      setAssigningBates(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!caseInfo) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Case not found.</p>
        <Link to="/cases" className="text-indigo-600 hover:underline mt-2 inline-block">Back to Cases</Link>
      </div>
    );
  }

  // Chart data
  const total = stats.total || 0;
  const responsiveRate = total > 0 ? ((stats.responsive || 0) / total * 100).toFixed(1) : 0;
  const privilegeRate = total > 0 ? ((stats.privileged || 0) / total * 100).toFixed(1) : 0;
  const reviewProgress = total > 0 ? ((stats.reviewed || 0) / total * 100).toFixed(0) : 0;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/cases')} className="text-gray-500 hover:text-gray-700">
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{caseInfo.case_name}</h1>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              {caseInfo.case_number && <span>Case #{caseInfo.case_number}</span>}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                caseInfo.status === 'active' ? 'bg-green-100 text-green-700' :
                caseInfo.status === 'produced' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {caseInfo.status}
              </span>
              {caseInfo.created_at && <span>Created {new Date(caseInfo.created_at).toLocaleDateString()}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/cases/${caseId}/review`)}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-1.5"
          >
            <PlayIcon className="w-4 h-4" />
            Start Review
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Total Docs', value: stats.total || 0, icon: DocumentTextIcon, color: 'text-gray-700 bg-gray-50' },
          { label: 'Reviewed', value: stats.reviewed || 0, icon: CheckCircleIcon, color: 'text-blue-700 bg-blue-50' },
          { label: 'Responsive', value: stats.responsive || 0, icon: CheckCircleIcon, color: 'text-green-700 bg-green-50' },
          { label: 'Non-Responsive', value: stats.non_responsive || 0, icon: DocumentTextIcon, color: 'text-gray-500 bg-gray-50' },
          { label: 'Privileged', value: stats.privileged || 0, icon: ShieldExclamationIcon, color: 'text-purple-700 bg-purple-50' },
          { label: 'Produced', value: stats.produced || 0, icon: DocumentArrowDownIcon, color: 'text-indigo-700 bg-indigo-50' },
        ].map(s => (
          <div key={s.label} className={`rounded-lg border border-gray-200 p-4 ${s.color.split(' ')[1]}`}>
            <div className="flex items-center gap-2 mb-2">
              <s.icon className={`w-5 h-5 ${s.color.split(' ')[0]}`} />
              <span className="text-xs text-gray-500">{s.label}</span>
            </div>
            <div className={`text-3xl font-bold ${s.color.split(' ')[0]}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Review progress bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Review Progress</h3>
          <span className="text-sm text-gray-500">{reviewProgress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-indigo-600 h-3 rounded-full transition-all"
            style={{ width: `${reviewProgress}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <span>{stats.reviewed || 0} reviewed</span>
          <span>{(stats.total || 0) - (stats.reviewed || 0)} remaining</span>
        </div>
      </div>

      {/* Two-column layout: rates + actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Key rates */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
            <ChartBarIcon className="w-4 h-4" />
            Key Metrics
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Responsiveness Rate</span>
                <span className="font-semibold text-green-700">{responsiveRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: `${responsiveRate}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Privilege Rate</span>
                <span className="font-semibold text-purple-700">{privilegeRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${privilegeRate}%` }} />
              </div>
            </div>
          </div>

          {/* Custodians */}
          {caseInfo.custodians && caseInfo.custodians.length > 0 && (
            <div className="mt-6">
              <h4 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <UserGroupIcon className="w-3.5 h-3.5" />
                Custodians
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {caseInfo.custodians.map(c => (
                  <span key={c} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Search terms */}
          {caseInfo.search_terms && caseInfo.search_terms.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <HashtagIcon className="w-3.5 h-3.5" />
                Search Terms
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {caseInfo.search_terms.map(t => (
                  <span key={t} className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Actions</h3>
          <div className="space-y-3">
            <button
              onClick={handleAnalyze}
              disabled={analyzing}
              className="w-full px-4 py-3 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {analyzing ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Running AI Analysis...
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4" />
                  Run AI Privilege Analysis
                </>
              )}
            </button>

            <button
              onClick={() => navigate(`/cases/${caseId}/review`)}
              className="w-full px-4 py-3 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center justify-center gap-2"
            >
              <DocumentTextIcon className="w-4 h-4" />
              Open Review Queue
            </button>

            <button
              onClick={handlePrivilegeLog}
              disabled={generatingLog}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <TableCellsIcon className="w-4 h-4" />
              {generatingLog ? 'Generating...' : 'Download Privilege Log (CSV)'}
            </button>

            <button
              onClick={handleAssignBates}
              disabled={assigningBates}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <HashtagIcon className="w-4 h-4" />
              {assigningBates ? 'Assigning...' : 'Assign Bates Numbers'}
            </button>

            <button
              onClick={() => navigate(`/cases/${caseId}/export`)}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center gap-2"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              Export Production Set
            </button>
          </div>
        </div>
      </div>

      {/* Production summary */}
      {production.bates_assigned > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Production Summary</h3>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-xl font-bold text-green-700">{production.to_produce || 0}</div>
              <div className="text-xs text-gray-500">To Produce</div>
            </div>
            <div>
              <div className="text-xl font-bold text-red-700">{production.to_withhold || 0}</div>
              <div className="text-xs text-gray-500">Withhold</div>
            </div>
            <div>
              <div className="text-xl font-bold text-amber-700">{production.to_redact || 0}</div>
              <div className="text-xs text-gray-500">Redact</div>
            </div>
            <div>
              <div className="text-xl font-bold text-indigo-700">{production.bates_assigned || 0}</div>
              <div className="text-xs text-gray-500">Bates Assigned</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
